import * as React from 'react'
import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import { TablePagination, Typography } from '@mui/material'
import { stableSort, getComparator, Order, HeadCell } from '../../../utils/tableFunctions'
import EnhancedTableHead from '../../../components/table/EnhancedTableHead'
import { RouteConstants } from '../../../navigation/navigation-types'
import CustomTableCell from '../../../components/common/CustomTableCell'
import { PlannedEventDto } from '../../../services/api/sharedTypes/Dashboard'
import { limitCharactersInText } from '../../../utils/global-utils'

const headCells: readonly HeadCell[] = [
    {
        id: 'clientName',
        numeric: false,
        disablePadding: false,
        label: 'Klient',
    },
    {
        id: 'startsAt',
        numeric: false,
        disablePadding: false,
        label: 'Datum od',
    },
    {
        id: 'endsAt',
        numeric: false,
        disablePadding: false,
        label: 'Datum do',
    },
    {
        id: 'name',
        numeric: false,
        disablePadding: false,
        label: 'Název',
    },
    {
        id: 'description',
        numeric: false,
        disablePadding: false,
        label: 'Popis',
    },
    {
        id: 'address',
        numeric: false,
        disablePadding: false,
        label: 'Místo',
    },
]

interface PlannedEventsTableProps {
    plannedEvents: PlannedEventDto[]
}

const PlannedEventsTable: React.FC<PlannedEventsTableProps> = ({ plannedEvents }: PlannedEventsTableProps) => {
    const [order, setOrder] = React.useState<Order>('desc')
    const [orderBy, setOrderBy] = React.useState<string>('startsAt')
    const [page, setPage] = React.useState(0)
    const [rowsPerPage, setRowsPerPage] = React.useState(10)

    const theme = useTheme()

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
    }

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    return (
        <>
            <Typography variant='body2' style={{ marginBottom: theme.spacing(3) }}>
                {plannedEvents.length} záznamů
            </Typography>
            <Box style={{ width: '100%' }}>
                <TableContainer>
                    <Table>
                        <EnhancedTableHead
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            headCells={headCells}
                            rowCount={plannedEvents.length}
                        />
                        <TableBody>
                            {stableSort(
                                plannedEvents.map((row) => {
                                    return {
                                        ...row,
                                        clientIdName: row.clientIdName.name,
                                        clientName: row.clientIdName.name,
                                        clientId: row.clientIdName.id ?? '',
                                        eventIdName: row.eventIdName.name,
                                        eventId: row.eventIdName.id ?? '',
                                        eventName: row.eventIdName.name,
                                        address: `${row.address?.street ?? ''} ${row.address?.zip ?? ''} ${row.address?.city ?? ''
                                            }`,
                                        startsAt: new Date(row.startsAt).getTime(),
                                        endsAt: new Date(row.endsAt).getTime(),
                                    }
                                }),
                                getComparator(order, orderBy)
                            )
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row, index: number) => {
                                    return (
                                        <TableRow hover tabIndex={-1} key={index} style={{ cursor: 'pointer' }}>
                                            <CustomTableCell
                                                maxWidth={300}
                                                style={{ minWidth: 110 }}
                                                route={RouteConstants.events + '/' + row.eventId}
                                                isText
                                            >
                                                {row.clientName}
                                            </CustomTableCell>
                                            <CustomTableCell
                                                style={{ width: 110 }}
                                                route={RouteConstants.events + '/' + row.eventId}
                                                isText
                                            >
                                                {new Date(row.startsAt).toLocaleDateString('cs')}
                                            </CustomTableCell>
                                            <CustomTableCell
                                                style={{ width: 110 }}
                                                route={RouteConstants.events + '/' + row.eventId}
                                                isText
                                            >
                                                {new Date(row.endsAt).toLocaleDateString('cs')}
                                            </CustomTableCell>
                                            <CustomTableCell
                                                maxWidth={200}
                                                route={RouteConstants.events + '/' + row.eventId}
                                                isText
                                            >
                                                {row.name}
                                            </CustomTableCell>
                                            <CustomTableCell route={RouteConstants.events + '/' + row.eventId} isText>
                                                {limitCharactersInText(row.description, 80)}
                                            </CustomTableCell>
                                            <CustomTableCell
                                                maxWidth={250}
                                                route={RouteConstants.events + '/' + row.eventId}
                                                isText
                                            >
                                                {row.address}
                                            </CustomTableCell>
                                        </TableRow>
                                    )
                                })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component='div'
                    count={plannedEvents.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Box>
        </>
    )
}

export default PlannedEventsTable
